import { useCallback, useEffect, useRef, useState } from "react";

const MIN = 50;
const MAX = 200;
const STORAGE_KEY = "stageModalZoom";

const clamp = (value) => Math.max(MIN, Math.min(MAX, Math.round(value)));

/** Ctrl/Cmd + scroll on desktop, two-finger pinch on touch. Level is remembered for the session. */
export const useZoomModal = (active = true) => {
  const [zoom, setZoom] = useState(() => {
    const saved = parseInt(sessionStorage.getItem(STORAGE_KEY) || "", 10);
    return Number.isFinite(saved) ? clamp(saved) : 100;
  });
  const containerRef = useRef(null);
  const pinchStart = useRef(null);

  const apply = useCallback((next) => {
    setZoom((current) => {
      const value = clamp(typeof next === "function" ? next(current) : next);
      sessionStorage.setItem(STORAGE_KEY, String(value));
      return value;
    });
  }, []);

  useEffect(() => {
    const node = containerRef.current;
    if (!active || !node) return;

    const onWheel = (event) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      apply((current) => current + (event.deltaY > 0 ? -10 : 10));
    };

    const distance = (touches) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const onTouchStart = (event) => {
      if (event.touches.length !== 2) return;
      pinchStart.current = { distance: distance(event.touches), zoom };
    };

    const onTouchMove = (event) => {
      if (event.touches.length !== 2 || !pinchStart.current) return;
      event.preventDefault();
      const scale = distance(event.touches) / pinchStart.current.distance;
      apply(pinchStart.current.zoom * scale);
    };

    const onTouchEnd = () => {
      pinchStart.current = null;
    };

    node.addEventListener("wheel", onWheel, { passive: false });
    node.addEventListener("touchstart", onTouchStart, { passive: true });
    node.addEventListener("touchmove", onTouchMove, { passive: false });
    node.addEventListener("touchend", onTouchEnd);
    return () => {
      node.removeEventListener("wheel", onWheel);
      node.removeEventListener("touchstart", onTouchStart);
      node.removeEventListener("touchmove", onTouchMove);
      node.removeEventListener("touchend", onTouchEnd);
    };
  }, [active, apply, zoom]);

  return {
    zoom,
    containerRef,
    setZoom: apply,
    zoomIn: () => apply((c) => c + 10),
    zoomOut: () => apply((c) => c - 10),
    resetZoom: () => apply(100),
    min: MIN,
    max: MAX,
  };
};

export default useZoomModal;
